export type FeaturedProductEntry = {
  productId: string;
  order: number;
  isActive: boolean;
};

export const MAX_FEATURED_PRODUCTS = 12;

export function parseFeaturedProductsFromSettings(
  settingsData: unknown,
): FeaturedProductEntry[] {
  if (!settingsData || typeof settingsData !== "object") return [];
  const featured = (settingsData as { featuredProducts?: unknown })
    .featuredProducts;
  if (!Array.isArray(featured)) return [];

  return featured
    .filter(
      (item): item is Record<string, unknown> =>
        item != null && typeof item === "object",
    )
    .map((item, index) => ({
      productId: String(item.productId ?? ""),
      order: typeof item.order === "number" ? item.order : index,
      isActive: item.isActive !== false,
    }))
    .filter((item) => item.productId.length > 0);
}

export function getActiveFeaturedProductIds(
  entries: FeaturedProductEntry[],
): string[] {
  return entries.filter((entry) => entry.isActive).map((entry) => entry.productId);
}

export function toggleFeaturedProduct(
  current: FeaturedProductEntry[],
  productId: string,
  markFeatured: boolean,
): FeaturedProductEntry[] {
  const withoutTarget = current
    .filter((entry) => entry.productId !== productId)
    .map((entry, index) => ({ ...entry, order: index }));

  if (!markFeatured) {
    return withoutTarget;
  }

  const activeCount = withoutTarget.filter((entry) => entry.isActive).length;
  if (activeCount >= MAX_FEATURED_PRODUCTS) {
    throw new Error(
      `You can feature at most ${MAX_FEATURED_PRODUCTS} products on your shop page.`,
    );
  }

  return [
    ...withoutTarget,
    { productId, order: withoutTarget.length, isActive: true },
  ];
}

export function toStorefrontFeaturedPayload(entries: FeaturedProductEntry[]) {
  return entries.map((entry, index) => ({
    productId: String(entry.productId),
    order: index,
    isActive: entry.isActive !== false,
  }));
}
